import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  map,
  startWith,
  distinctUntilChanged,
  switchMap,
  of,
  combineLatest,
  debounceTime,
} from 'rxjs';
import { ServersService } from '../../../../core/services/servers.service';
import { WebsitesService } from '../../../../core/services/websites.service';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AddDnsZoneDialogResult } from '../../models/add-dns-zone.model';

@Component({
  selector: 'app-add-dns-zone-dialog',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatCheckboxModule,
    InputField,
    FontAwesomeModule,
  ],
  templateUrl: './add-dns-zone-dialog.component.html',
  styleUrl: './add-dns-zone-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDnsZoneDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AddDnsZoneDialogComponent>);

  private readonly destroyRef = inject(DestroyRef);

  private readonly serversService = inject(ServersService);

  private readonly websitesService = inject(WebsitesService);

  readonly websiteSearchText = signal<string | undefined>('');

  readonly serverSearchText = signal('');

  readonly form = new FormGroup({
    server: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    websiteId: new FormControl(
      { value: '', disabled: true },
      {
        nonNullable: true,
        validators: Validators.required,
      },
    ),
    overwrite: new FormControl(true, {
      nonNullable: true,
    }),
  });

  readonly servers$ = combineLatest([
    this.serversService.getAllServers(),
    toObservable(this.serverSearchText),
  ]).pipe(
    map(([servers, searchText]) =>
      servers
        .filter((server) => server.hostname.toLowerCase().includes(searchText.trim().toLowerCase()))
        .map((server) => ({
          label: server.hostname,
          value: server._id,
        })),
    ),
  );

  readonly websites$ = combineLatest([
    this.form.controls.server.valueChanges.pipe(
      startWith(this.form.controls.server.value),
      distinctUntilChanged(),
    ),
    toObservable(this.websiteSearchText).pipe(
      map((searchText) => searchText ?? ''),
      debounceTime(300),
      distinctUntilChanged(),
    ),
  ]).pipe(
    switchMap(([serverId, searchText]) => {
      if (!serverId) {
        return of([]);
      }

      return this.websitesService
        .searchWebsites({
          serverSearch: serverId,
          domainSearch: searchText?.trim() || undefined,
        })
        .pipe(
          map((response) =>
            response.Websites.map((website) => ({
              label: website.domain,
              value: String(website.id),
            })),
          ),
        );
    }),
  );

  readonly websiteOptions = toSignal(this.websites$, {
    initialValue: [],
  });

  constructor() {
    this.form.controls.server.valueChanges.pipe(takeUntilDestroyed()).subscribe((serverId) => {
      this.form.controls.websiteId.reset();
      this.websiteSearchText.set('');

      if (serverId) {
        this.form.controls.websiteId.enable();
      } else {
        this.form.controls.websiteId.disable();
      }
    });
  }

  ngOnInit(): void {
    this.form.controls.server.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((serverId) => {
        this.form.controls.websiteId.reset();
        this.websiteSearchText.set('');

        if (serverId) {
          this.form.controls.websiteId.enable();
        } else {
          this.form.controls.websiteId.disable();
        }
      });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();

    const domain =
      this.websiteOptions().find((website) => website.value === value.websiteId)?.label ?? '';

    const result: AddDnsZoneDialogResult = {
      server: value.server,
      websiteId: Number(value.websiteId),
      adminOnly: false,
      overwrite: value.overwrite,
      domain,
    };

    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }
}
